import { speechSynthesisRate } from "@lib/lessonPreferences";

/**
 * iOS Safari treats speak() outside a user gesture as a silent no-op: onstart, onend
 * and onerror all never fire. Callers gate UI on the returned promise, so it has to
 * settle on its own if speech never starts. Only the start is bounded; once onstart
 * fires the utterance runs to its natural end, however long it is.
 */
export const SPEECH_START_TIMEOUT_MS = 3_000;

let passageAudioElement: HTMLAudioElement | null = null;
let activeAudio: HTMLAudioElement | null = null;
let activeObjectUrl: string | null = null;
let notifiedPlaying = false;
let playbackToken = 0;
const stopListeners = new Set<() => void>();
const playListeners = new Set<() => void>();

export function subscribeLessonAudioPlay(listener: () => void): () => void {
  playListeners.add(listener);
  return () => playListeners.delete(listener);
}

export function subscribeLessonAudioStop(listener: () => void): () => void {
  stopListeners.add(listener);
  return () => stopListeners.delete(listener);
}

function notifyAudioPlaying() {
  notifiedPlaying = true;
  for (const listener of playListeners) {
    listener();
  }
}

/** Stop events only fire after a matching play event, so listeners see clean play→stop pairs. */
function notifyAudioStopped() {
  if (!notifiedPlaying) return;
  notifiedPlaying = false;
  for (const listener of stopListeners) {
    listener();
  }
}

export function registerPassageAudioElement(audio: HTMLAudioElement | null) {
  if (passageAudioElement) {
    passageAudioElement.removeEventListener("play", notifyAudioPlaying);
    passageAudioElement.removeEventListener("ended", notifyAudioStopped);
    passageAudioElement.removeEventListener("pause", notifyAudioStopped);
  }
  passageAudioElement = audio;
  if (audio) {
    audio.addEventListener("play", notifyAudioPlaying);
    audio.addEventListener("ended", notifyAudioStopped);
    audio.addEventListener("pause", notifyAudioStopped);
  }
}

export function stopLessonAudio() {
  playbackToken += 1;

  if (passageAudioElement) {
    passageAudioElement.pause();
    passageAudioElement.currentTime = 0;
  }

  if (activeAudio) {
    const audio = activeAudio;
    activeAudio = null;
    // Pausing resolves the in-flight playback promise via its pause handler.
    audio.pause();
  }
  releaseActiveObjectUrl();

  if (typeof window !== "undefined" && window.speechSynthesis?.speaking) {
    window.speechSynthesis.cancel();
  }

  notifyAudioStopped();
}

function releaseActiveObjectUrl() {
  if (activeObjectUrl) {
    URL.revokeObjectURL(activeObjectUrl);
    activeObjectUrl = null;
  }
}

/**
 * All clip playback goes through an HTMLAudioElement: slowed playback keeps its
 * pitch (preservesPitch), and blocked autoplay surfaces as a catchable rejection
 * instead of silently playing nothing. The play notification fires only once
 * audio is actually audible.
 */
async function playAudioElement(
  audio: HTMLAudioElement,
  playbackRate: number,
  token: number,
): Promise<void> {
  audio.playbackRate = playbackRate;
  audio.preservesPitch = true;
  activeAudio = audio;
  try {
    const finished = new Promise<void>((resolve) => {
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
      audio.onpause = () => resolve();
    });
    await audio.play();
    if (token === playbackToken) {
      notifyAudioPlaying();
    }
    await finished;
  } finally {
    if (activeAudio === audio) {
      activeAudio = null;
    }
    if (token === playbackToken) {
      notifyAudioStopped();
    }
  }
}

export async function playLessonAudioUrl(url: string, playbackRate = 1): Promise<void> {
  stopLessonAudio();
  await playAudioElement(new Audio(url), playbackRate, playbackToken);
}

export async function playLessonAudioBuffer(buffer: ArrayBuffer, playbackRate = 1): Promise<void> {
  stopLessonAudio();
  const token = playbackToken;
  const objectUrl = URL.createObjectURL(new Blob([buffer], { type: "audio/mpeg" }));
  activeObjectUrl = objectUrl;
  try {
    await playAudioElement(new Audio(objectUrl), playbackRate, token);
  } finally {
    if (activeObjectUrl === objectUrl) {
      URL.revokeObjectURL(objectUrl);
      activeObjectUrl = null;
    }
  }
}

export async function playLessonSpeechSynthesis(text: string, tutorSpeechSpeed = 1): Promise<void> {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return;
  }

  stopLessonAudio();
  const token = playbackToken;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = speechSynthesisRate(tutorSpeechSpeed);

  let startWatchdog: ReturnType<typeof setTimeout> | undefined;
  try {
    await new Promise<void>((resolve) => {
      startWatchdog = setTimeout(() => {
        window.speechSynthesis.cancel();
        resolve();
      }, SPEECH_START_TIMEOUT_MS);
      utterance.onstart = () => {
        clearTimeout(startWatchdog);
        if (token === playbackToken) {
          notifyAudioPlaying();
        }
      };
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      window.speechSynthesis.speak(utterance);
    });
  } finally {
    clearTimeout(startWatchdog);
    if (token === playbackToken) {
      notifyAudioStopped();
    }
  }
}
